import ShopDTO from "@/dto/shop/ShopDTO";
import ShopType from "@/enum/ShopType";
import OrderSort from "@/scenes/OrderManagement/enum/OrderSort";
import OrderTab from "@/scenes/OrderManagement/enum/OrderTab";
import {
    OrderSortPrioritizationRule,
    OrderSortPrioritizationSetting,
} from "@/services/api/orderSortPrioritizationSetting";
import { showDeadlineShippingOrderTabs } from "@/utils/OrderTabUtil";
import { ShippingDeadlineShopTypes } from "@/utils/ShopUtils";

export function getDefaultOrderSortOptions(shopType: ShopType, tab: OrderTab) {
    if (tab === OrderTab.ABNORMAL) {
        return [OrderSort.ABNORMAL_RECORD_TIME_DESC, OrderSort.ABNORMAL_RECORD_TIME_ASC];
    } else {
        return [
            {
                orderSort: OrderSort.IMPORT,
                condition: (shopType) => [ShopType.INDEPENDENT_MANUAL, ShopType.MANUAL].includes(shopType),
            },

            {
                orderSort: OrderSort.ORDER_TIME_DESC,
                condition: () => true,
            },
            {
                orderSort: OrderSort.ORDER_TIME_ASC,
                condition: () => true,
            },

            {
                orderSort: OrderSort.SHIP_TIME_ASC,
                condition: (_s, tab) => [OrderTab.SHIPPED, OrderTab.SUCCESS].includes(tab),
            },
            {
                orderSort: OrderSort.SHIP_TIME_DESC,
                condition: (_s, tab) => [OrderTab.SHIPPED, OrderTab.SUCCESS].includes(tab),
            },

            {
                orderSort: OrderSort.PAY_TIME_ASC,
                condition: (_s, tab) => [OrderTab.PENDING_SHIPMENT, OrderTab.ON_HOLD].includes(tab),
            },
            {
                orderSort: OrderSort.PAY_TIME_DESC,
                condition: (_s, tab) =>
                    [OrderTab.PENDING_SHIPMENT, OrderTab.SHIPPED, OrderTab.SUCCESS, OrderTab.ON_HOLD].includes(tab),
            },

            {
                orderSort: OrderSort.PROMISE_SHIPPING_TIME_ASC,
                condition: (shopType, tab) =>
                    shopType === ShopType.PINDUODUO && [OrderTab.PENDING_SHIPMENT, OrderTab.ON_HOLD].includes(tab),
            },

            {
                orderSort: OrderSort.LAST_SHIPPING_TIME_ASC,
                condition: (shopType, tab) =>
                    (ShippingDeadlineShopTypes.includes(shopType) || !shopType) &&
                    showDeadlineShippingOrderTabs.includes(tab),
            },

            {
                orderSort: OrderSort.SIMPLE_TITLE_OTHERWISE_SKU_PROPERTIES,
                condition: (_s, tab) => [OrderTab.PENDING_SHIPMENT, OrderTab.ON_HOLD].includes(tab),
            },
            {
                orderSort: OrderSort.PRODUCT_SKU_EXTERNAL_ID,
                condition: (_s, tab) => [OrderTab.PENDING_SHIPMENT, OrderTab.ON_HOLD].includes(tab),
            },
            {
                orderSort: OrderSort.SKU_EXTERNAL_ID,
                condition: (_s, tab) => [OrderTab.PENDING_SHIPMENT, OrderTab.ON_HOLD].includes(tab),
            },

            {
                orderSort: OrderSort.PRODUCT_SKU_COLOR_SIZE,
                condition: (_s, tab) =>
                    [OrderTab.PENDING_SHIPMENT, OrderTab.SHIPPED, OrderTab.SUCCESS, OrderTab.ON_HOLD].includes(tab),
            },
            {
                orderSort: OrderSort.ORDER_ITEMS,
                condition: (_s, tab) =>
                    [OrderTab.PENDING_SHIPMENT, OrderTab.SHIPPED, OrderTab.SUCCESS, OrderTab.ON_HOLD].includes(tab),
            },

            {
                orderSort: OrderSort.ORDER_ITEM_QUANTITY,
                condition: () => true,
            },
            {
                orderSort: OrderSort.ORDER_ITEM_QUANTITY_DESC,
                condition: () => true,
            },
            {
                orderSort: OrderSort.ORDER_WEIGHT,
                condition: () => true,
            },

            {
                orderSort: OrderSort.PAYMENT_AMOUNT_DESC,
                condition: (_s, tab) => [OrderTab.PENDING_SHIPMENT, OrderTab.ON_HOLD].includes(tab),
            },
            {
                orderSort: OrderSort.BUYER_NICKNAME,
                condition: (_s, tab) => [OrderTab.PENDING_SHIPMENT, OrderTab.ON_HOLD, OrderTab.SHIPPED].includes(tab),
            },

            {
                orderSort: OrderSort.ORDER_CONSIGNEE_REGION,
                condition: () => true,
            },

            {
                orderSort: OrderSort.SEQUENCE_NO,
                condition: (_s, tab) =>
                    [OrderTab.PENDING_SHIPMENT, OrderTab.SHIPPED, OrderTab.SUCCESS, OrderTab.ON_HOLD].includes(tab),
            },
        ]
            .filter(({ condition }) => condition(shopType, tab))
            .map(({ orderSort }) => orderSort);
    }
}

/**
 * 根据优先级规则对排序选项进行排序。
 * @param prioritizationRules 优先级规则列表
 * @returns 排序后的订单排序选项数组
 */
export function getSortedOrderSortOptions(
    defaultOrderSortOptions: OrderSort[],
    prioritizationRules: OrderSortPrioritizationRule[],
): OrderSort[] {
    if (!prioritizationRules?.length) return defaultOrderSortOptions;

    const pendingSortOptions = [...defaultOrderSortOptions];
    // 创建 OrderSort 到优先级 index 的映射表
    const sortPriorityMap = new Map<OrderSort, number>();
    for (const rule of prioritizationRules) {
        sortPriorityMap.set(rule.orderSort, rule.index);
    }
    // 分组：有优先级和无优先级
    const withPriority: OrderSort[] = [];
    // 后续业务新增的OrderTab
    const withoutPriority: OrderSort[] = [];
    for (const sortOption of pendingSortOptions) {
        if (sortPriorityMap.has(sortOption)) {
            withPriority.push(sortOption);
        } else {
            withoutPriority.push(sortOption);
        }
    }
    // 有优先级的按 index 升序排列
    const sortedWithPriority = [...withPriority].sort((a, b) => {
        const indexA = sortPriorityMap.get(a);
        const indexB = sortPriorityMap.get(b);
        return indexA - indexB;
    });
    // 合并结果
    return [...sortedWithPriority, ...withoutPriority];
}

/**
 * 根据当前 tab 和店铺类型，生成可用的订单排序选项。
 */
export function generateOrderSortOptions(
    tab: OrderTab,
    shopType: ShopType,
    newPrioritizationRule: OrderSortPrioritizationRule[],
): OrderSort[] {
    const defaultOrderSortOptions = getDefaultOrderSortOptions(shopType, tab);

    try {
        if (newPrioritizationRule) {
            return getSortedOrderSortOptions(defaultOrderSortOptions, newPrioritizationRule);
        }
    } catch {}

    return defaultOrderSortOptions;
}
